import { CURRENT_SCHOOL_YEAR } from '../../../shared/constant/schoolYears';
import { getSectionTermAssessments, getTransmutationTable } from '../services/gradesService';
import { downloadBuffer, fullName, teacherName } from './gradeSummaryExport';

// The school's E-Class Record term sheet (public/templates), filled in for one section and term.
// Only names, highest possible scores, scores, weights and the header are written: the template's
// own formulas work out the totals, PS, WS, initial, transmuted and letter grades.
const TEMPLATE_URL = `${process.env.PUBLIC_URL}/templates/e-class-record-template.xlsx`;

const CELLS = {
  term: 'A7',
  schoolYear: 'Z5',
  gradeSection: 'K7',
  teacher: 'S7',
  track: 'S8',
  subject: 'Z7',
  subjectType: 'Z8',
};
const TERM_LABELS = { 1: 'FIRST TERM', 2: 'SECOND TERM', 3: 'THIRD TERM' };

// Score columns per component, the row-11 cell holding its weight (as a fraction) and its heading.
// Examination items fill Summative Test 1, Summative Test 2 and the Term Examination, in order.
const COMPONENTS = {
  written_work: { label: 'Written Works', columns: ['F', 'G', 'H', 'I', 'J'], weightCell: 'M11', headingCell: 'F9', heading: 'WRITTEN WORKS', weightField: 'ww_weight' },
  performance_task: { label: 'Performance Tasks', columns: ['N', 'O', 'P'], weightCell: 'S11', headingCell: 'N9', heading: 'PERFORMANCE TASKS', weightField: 'pt_weight' },
  examination: { label: 'Summative Tests and Term Examination', columns: ['T', 'U', 'V'], weightCell: 'Y11', headingCell: 'T9', heading: 'SUMMATIVE TESTS & TERM EXAMINATION', weightField: 'exam_weight' },
};
const MAX_SCORE_ROW = 11;

// Each list has 50 numbered rows.
const LISTS = { M: { firstRow: 13, label: 'male' }, F: { firstRow: 64, label: 'female' } };
const ROWS_PER_LIST = 50;
const NAME_COLUMN = 'B';

// The template's TRANSMUTATION name covers the Transmutation sheet from row 2.
const TRANSMUTATION_SHEET = 'Transmutation';
const TRANSMUTATION_NAME = 'TRANSMUTATION';

function mostCommon(values) {
  const counts = {};
  values.filter(Boolean).forEach((v) => {
    counts[v] = (counts[v] || 0) + 1;
  });
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] ?? '';
}

// Downloads the filled-in E-Class Record for one section's `students` in `term`. Resolves to the
// students left out because they have no sex recorded (the form only has Male and Female lists).
export async function exportEClassRecord({ subject, section, term, students }) {
  const [{ default: ExcelJS }, response, assessments, transmutation] = await Promise.all([
    import('exceljs'),
    fetch(TEMPLATE_URL),
    getSectionTermAssessments(subject.id, term, section),
    getTransmutationTable(),
  ]);
  if (!response.ok) throw new Error('Could not load the E-Class Record template.');

  const byComponent = Object.fromEntries(
    Object.keys(COMPONENTS).map((key) => [key, assessments.filter((a) => a.component === key)])
  );
  Object.entries(COMPONENTS).forEach(([key, { label, columns }]) => {
    if (byComponent[key].length > columns.length) {
      throw new Error(
        `The form has room for ${columns.length} ${label} in a term, but ${section} has ${byComponent[key].length} in Term ${term}.`
      );
    }
  });

  const byName = (a, b) => a.lastname.localeCompare(b.lastname) || a.firstname.localeCompare(b.firstname);
  const lists = {
    M: students.filter((s) => s.gender === 'M').sort(byName),
    F: students.filter((s) => s.gender === 'F').sort(byName),
  };
  Object.entries(lists).forEach(([gender, list]) => {
    if (list.length > ROWS_PER_LIST) {
      throw new Error(
        `The form has room for ${ROWS_PER_LIST} ${LISTS[gender].label} students, but ${section} has ${list.length}.`
      );
    }
  });

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());
  // Nothing in the template has a calculated value yet, so Excel works the formulas out on opening.
  workbook.calcProperties.fullCalcOnLoad = true;
  const sheet = workbook.worksheets[0];
  sheet.name = `TERM ${term}`;

  sheet.getCell(CELLS.term).value = TERM_LABELS[term];
  sheet.getCell(CELLS.schoolYear).value = subject.school_year || CURRENT_SCHOOL_YEAR;
  sheet.getCell(CELLS.gradeSection).value = `${subject.grade_level} - ${section}`;
  sheet.getCell(CELLS.teacher).value = teacherName(subject.teachers);
  sheet.getCell(CELLS.track).value = mostCommon(students.map((s) => s.track));
  sheet.getCell(CELLS.subject).value = subject.name;
  sheet.getCell(CELLS.subjectType).value = subject.category;

  Object.entries(COMPONENTS).forEach(([key, { columns, weightCell, headingCell, heading, weightField }]) => {
    const weight = Number(subject[weightField]);
    sheet.getCell(headingCell).value = `${heading} (${weight}%)`;
    sheet.getCell(weightCell).value = weight / 100;
    byComponent[key].forEach((assessment, i) => {
      sheet.getCell(`${columns[i]}${MAX_SCORE_ROW}`).value = Number(assessment.max_score);
    });
  });

  Object.entries(lists).forEach(([gender, list]) => {
    list.forEach((student, i) => {
      const row = LISTS[gender].firstRow + i;
      sheet.getCell(`${NAME_COLUMN}${row}`).value = fullName(student);
      Object.entries(COMPONENTS).forEach(([key, { columns }]) => {
        byComponent[key].forEach((assessment, a) => {
          const score = assessment.assessment_scores.find((s) => s.student_id === student.id);
          sheet.getCell(`${columns[a]}${row}`).value = score ? Number(score.score) : null;
        });
      });
    });
  });

  const table = workbook.getWorksheet(TRANSMUTATION_SHEET);
  transmutation.forEach((band, i) => {
    table.getCell(`A${i + 2}`).value = Number(band.lower_bound);
    table.getCell(`B${i + 2}`).value = band.transmuted;
  });
  workbook.definedNames.model = [
    ...workbook.definedNames.model.filter((n) => n.name !== TRANSMUTATION_NAME),
    { name: TRANSMUTATION_NAME, ranges: [`${TRANSMUTATION_SHEET}!$A$2:$B$${transmutation.length + 1}`] },
  ];

  const safe = (text) => String(text).replace(/[\\/:*?"<>|]+/g, '').trim();
  downloadBuffer(
    await workbook.xlsx.writeBuffer(),
    `E-Class Record - ${safe(subject.name)} - ${safe(`${subject.grade_level}-${section}`)} - Term ${term}.xlsx`
  );

  return students.filter((s) => s.gender !== 'M' && s.gender !== 'F');
}
